import { ClerkProvider, useAuth, useClerk } from "@clerk/react";
import * as Sentry from "@sentry/react";
import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { createRoot } from "react-dom/client";
import { useEffect, useMemo, type ReactElement } from "react";
import { Toaster, TooltipProvider } from "@ndd/ui";
import { BreadcrumbProvider } from "@/components/v2/breadcrumb";
import {
  isAccessDeniedError,
  isNetworkRequestError,
  isNotFoundError,
  isUnauthenticatedError,
  setUnauthenticatedHandler,
} from "@/lib/api";
import { decideApiErrorCapture } from "@/lib/api-error-capture";
import { router } from "./router";
import { createClientSentryOptions } from "./sentry";
import "./styles.css";

const clerkPublishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as
  | string
  | undefined;
const sentryDsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

if (sentryDsn) {
  Sentry.init(
    createClientSentryOptions({
      dsn: sentryDsn,
      environment:
        (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ??
        import.meta.env.MODE,
      release: import.meta.env.VITE_SENTRY_RELEASE as string | undefined,
    })
  );
}

function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (isNotFoundError(error)) return;

        if (isAccessDeniedError(error)) return;

        if (isNetworkRequestError(error)) return;

        // Explicit per the ticket. decideApiErrorCapture also refuses 401 so
        // callers without this filter chain (the presence heartbeat) are covered.
        if (isUnauthenticatedError(error)) return;

        const decision = decideApiErrorCapture(error);

        if (!decision.capture) return;

        Sentry.captureException(error, {
          level: decision.level,
          tags: {
            surface: "api",
            source: "client",
            query: String(query.queryKey[0]),
            status: decision.status,
          },
        });
      },
    }),
    defaultOptions: {
      queries: { retry: false, staleTime: 15_000 },
      mutations: { retry: false },
    },
  });
}

const root = createRoot(document.getElementById("root") as HTMLElement);

if (!clerkPublishableKey) {
  root.render(<ConfigurationRequired />);
} else {
  root.render(
    <ClerkProvider publishableKey={clerkPublishableKey} afterSignOutUrl="/sign-in">
      <IdentityScopedApp />
    </ClerkProvider>
  );
}

function IdentityScopedApp(): ReactElement | null {
  const { isLoaded, userId } = useAuth({ treatPendingAsSignedOut: false });
  const clerk = useClerk();
  const queryClient = useMemo(() => createQueryClient(), [userId]);

  useEffect(() => {
    setUnauthenticatedHandler(() => {
      void clerk.signOut({ redirectUrl: "/sign-in" });
    });

    return () => setUnauthenticatedHandler(null);
  }, [clerk]);

  if (!isLoaded) return null;

  return (
    <QueryClientProvider key={userId ?? "signed-out"} client={queryClient}>
      <BreadcrumbProvider>
        <TooltipProvider>
          <RouterProvider router={router} />
        </TooltipProvider>
      </BreadcrumbProvider>
      <Toaster />
    </QueryClientProvider>
  );
}

function ConfigurationRequired(): ReactElement {
  return (
    <main className="min-h-screen flex items-center justify-center bg-background">
      <div className="max-w-md text-center px-4">
        <h1 className="text-xl font-semibold text-foreground font-sans mb-2">
          AlwaysAI configuration required
        </h1>
        <p className="text-muted-foreground text-sm leading-relaxed">
          Clerk is required before the application can load.
        </p>
      </div>
    </main>
  );
}
